// Global Instructions Rule Applied!
// Shape of GET /funds/:id/notice-settings (view__fund_notice_settings) and of
// the two notice PATCH routes: what is about the capital call NOTICE rather
// than the fund — the layout, the wording overrides and the bank account —
// plus the active layouts and, from the notice builder, the text blocks a
// layout prints and the merge fields they may use. The fund's own facts a
// notice prints (general partner, address, LPA sections, notice periods,
// sender) are funds columns in the fund terms record.
import { z } from 'zod'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'

const TextBlocksSchema = z.record(z.string(), z.string().nullable())

export const FundNoticeSettingsRowSchema = z
  .object({
    fund_notice_settings__id: z.string().nullish(),
    // null = the default layout
    fund_notice_settings__notice_template_id: z.number().nullish(),
    // the email design template the notice email is drafted from; null = the
    // default (prog__default_email_template_id)
    fund_notice_settings__email_design_template_id: z.string().nullish(),
    // block key -> the fund's wording; a missing or blank key keeps the default
    fund_notice_settings__text_overrides: TextBlocksSchema.nullish()
  })
  .strict()

export const FundBankAccountRowSchema = z
  .object({
    fund_bank_accounts__id: z.string().nullish(),
    fund_bank_accounts__bank_name: z.string().nullish(),
    fund_bank_accounts__bank_address: z.string().nullish(),
    fund_bank_accounts__bank_phone: z.string().nullish(),
    fund_bank_accounts__aba_routing: z.string().nullish(),
    fund_bank_accounts__account_number: z.string().nullish(),
    fund_bank_accounts__swift_bic: z.string().nullish(),
    fund_bank_accounts__beneficiary_name: z.string().nullish(),
    fund_bank_accounts__beneficiary_address: z.string().nullish()
  })
  .strict()

export const NoticeTemplateSchema = z
  .object({
    id: z.number(),
    code: z.string(),
    label: z.string(),
    description: z.string().nullish(),
    is_default: z.boolean(),
    text_blocks: TextBlocksSchema.nullish()
  })
  .strict()

export const FundNoticeSettingsSchema = z
  .object({
    fund: z
      .object({
        funds__id: z.string(),
        funds__name: z.string().nullish(),
        funds__legal_name: z.string().nullish()
      })
      .strict(),
    settings: FundNoticeSettingsRowSchema,
    bank_account: FundBankAccountRowSchema,
    templates: z.array(NoticeTemplateSchema),
    // the email design templates the notice email can be drafted from
    email_templates: z.array(z.object({ id: z.string(), name: z.string() }).strict()),
    prog__default_email_template_id: z.string().nullish(),
    // the layout that applies: the fund's choice, else the default
    prog__template_id: z.number().nullish(),
    // code-owned, from the notice builder
    text_blocks: z.array(z.object({ key: z.string(), label: z.string(), help: z.string().nullish() }).strict()),
    merge_fields: z.array(z.object({ key: z.string(), description: z.string() }).strict())
  })
  .strict()

export const FundNoticeSettingsRowFields = makeFieldKeys(FundNoticeSettingsRowSchema)
export const FundBankAccountRowFields = makeFieldKeys(FundBankAccountRowSchema)

export type FundNoticeSettings = z.infer<typeof FundNoticeSettingsSchema>
export type NoticeTemplate = z.infer<typeof NoticeTemplateSchema>

export function validateFundNoticeSettings(data: unknown): FundNoticeSettings {
  try {
    return validateWithZodSchema(FundNoticeSettingsSchema, data, 'fund notice settings')
  } catch (error) {
    throw new Error(`Fund notice settings validation failed: ${error}`)
  }
}
